import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
  LineChart, Line, CartesianGrid,
} from "recharts";
import {
  Buildings, CurrencyEur, Handshake, Users, CalendarCheck, Sparkle, Bell, Plus,
  UserPlus, CalendarPlus,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { eur, eurCompact, num, fdatetime } from "@/lib/format";
import { PageHeader, StatCard } from "@/components/common";
import { ScoreRing, PriorityChip, WhyScore } from "@/components/ScoreCard";
import { placeImage } from "@/lib/placeImage";
import { Button } from "@/components/ui/button";
import { useTranslation } from 'react-i18next'

const GOLD = "#C5A059";
const NAVY = "#0B2545";
const PIE = [NAVY, GOLD, "#3b82c4", "#2f9e6f", "#c9772f", "#8a6d2f", "#64748b"];

export default function Dashboard() {
  const { t } = useTranslation()
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/dashboard")).data,
  });
  const { data: topLocations = [] } = useQuery({
    queryKey: ["places-top"],
    queryFn: async () => (await api.get("/places/top?limit=6")).data,
  });
  const { data: priorityOpps = [] } = useQuery({
    queryKey: ["priority-opps"],
    queryFn: async () => (await api.get("/scoring/priority-opportunities?limit=6")).data,
  });
  const { data: matchDash } = useQuery({
    queryKey: ["matching-dashboard"],
    queryFn: async () => (await api.get("/matching/dashboard?limit=6")).data,
  });

  if (isLoading || !data)
    return (
      <div className="flex items-center justify-center py-40">
        <div className="animate-spin h-8 w-8 border-2 border-[#0B2545] border-t-transparent rounded-full" />
      </div>
    );

  const quick = [
    { label: t('addProperty2', 'Add property'), icon: Plus, to: "/properties?new=1", testid: "quick-add-property" },
    { label: t('addContact', 'Add contact'), icon: UserPlus, to: "/contacts?new=1", testid: "quick-add-contact" },
    { label: t('addOpportunity2', 'Add opportunity'), icon: Handshake, to: "/pipeline?new=1", testid: "quick-add-opportunity" },
    { label: t('scheduleVisit', 'Schedule visit'), icon: CalendarPlus, to: "/tasks?new=1", testid: "quick-schedule-visit" },
    { label: t('messages', 'Messages'), icon: Bell, to: "/messages", testid: "quick-messages" },
  ];

  return (
    <div>
      <PageHeader
        title="Dashboard"
        subtitle="Portfolio overview and daily priorities"
        actions={
          <div className="flex flex-wrap gap-2">
            {quick.map((q) => (
              <Button
                key={q.label}
                onClick={() => navigate(q.to)}
                data-testid={q.testid}
                variant="outline"
                className="rounded-sm border-slate-300 hover:border-[#C5A059] hover:text-[#C5A059] transition-colors"
              >
                <q.icon size={16} className="mr-1.5" /> {q.label}
              </Button>
            ))}
          </div>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        <StatCard label={t('totalProperties', 'Total Properties')} value={num(data.total_properties)} icon={Buildings} testid="stat-total-properties" onClick={() => navigate('/properties')} sub={t('availableAvailable', '{{Available}} available', { Available: data.status.Available })} delay={0} />
        <StatCard label={t('portfolioValue', 'Portfolio Value')} value={eurCompact(data.portfolio_value)} icon={CurrencyEur} accent testid="stat-portfolio-value" onClick={() => navigate('/properties')} sub="Estimated total" delay={0.05} />
        <StatCard label={t('potentialCommission', 'Potential Commission')} value={eurCompact(data.commission_value)} icon={Handshake} accent testid="stat-commission" onClick={() => navigate('/pipeline')} sub="Across portfolio" delay={0.1} />
        <StatCard label={t('buyersInvestors', 'Buyers & Investors')} value={num(data.active_buyers + data.active_investors)} icon={Users} testid="stat-buyers" onClick={() => navigate('/contacts?types=Buyer,Investor')} sub={t('active_investorsInvestors', '{{active_investors}} investors', { active_investors: data.active_investors })} delay={0.15} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-6">
        <StatCard label={t('followupsToday', 'Follow-ups Today')} value={num(data.follow_ups_today.length)} icon={CalendarCheck} testid="stat-followups" onClick={() => navigate('/tasks?due=today')} delay={0.2} />
        <StatCard label={t('upcomingVisits', 'Upcoming Visits')} value={num(data.upcoming_visits)} icon={CalendarCheck} testid="stat-visits" onClick={() => navigate('/tasks?type=visit')} delay={0.22} />
        <StatCard label={t('newOpportunities', 'New Opportunities')} value={num(data.new_opportunities)} icon={Sparkle} accent testid="stat-new-opps" onClick={() => navigate('/pipeline?filter=new')} delay={0.24} />
        <StatCard label={t('offersAwaiting', 'Offers Awaiting')} value={num(data.offers_awaiting)} icon={Bell} testid="stat-offers" onClick={() => navigate('/pipeline?filter=offers')} delay={0.26} />
      </div>

      {priorityOpps.length > 0 && (
        <div className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sparkle size={20} className="text-[#C5A059]" weight="fill" />
              <h3 className="font-serif text-xl text-slate-800">{t('scoring.priorityOpportunities', 'Priority Opportunities')}</h3>
            </div>
            <button onClick={() => navigate('/pipeline')} data-testid="dashboard-all-pipeline" className="text-sm text-[#C5A059] hover:underline">{t('scoring.viewAll', 'View all')}</button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="priority-opportunities">
            {priorityOpps.map(({ opportunity: o, score }) => (
              <div key={o.id} data-testid={`priority-opp-${o.id}`} className="bg-white border border-slate-200 rounded-sm p-5 shadow-[0_8px_30px_rgba(11,37,69,0.04)]">
                <div className="flex items-start gap-3">
                  <ScoreRing value={score.overall} priority={score.priority} size={46} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900 leading-snug truncate">{o.title}</div>
                    {o.property_title && <div className="text-xs text-slate-400 truncate">{o.property_title}</div>}
                    <div className="flex items-center gap-2 mt-1.5">
                      <PriorityChip priority={score.priority} />
                      <span className="text-[11px] text-slate-500">{o.stage}</span>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-sm font-semibold text-[#0B2545]">{o.value ? eurCompact(o.value) : '—'}</div>
                    <div className="text-[11px] text-slate-400">{score.deal_probability}%</div>
                  </div>
                </div>
                <WhyScore score={score} testid={`priority-why-${o.id}`} />
              </div>
            ))}
          </div>
        </div>
      )}

      {matchDash?.new_matches?.length > 0 && (
        <div className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Bell size={20} className="text-[#C5A059]" weight="fill" />
              <h3 className="font-serif text-xl text-slate-800">{t('matching.newMatches', 'New Matches')}</h3>
              {matchDash.unread_alerts > 0 && <span className="text-xs bg-[#C5A059] text-white rounded-full px-2 py-0.5" data-testid="dashboard-alerts-count">{matchDash.unread_alerts}</span>}
            </div>
            <button onClick={() => navigate('/requirements')} data-testid="dashboard-all-requirements" className="text-sm text-[#C5A059] hover:underline">{t('navigation.requirements', 'Requirements')}</button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="dashboard-new-matches">
            {matchDash.new_matches.map((mm) => (
              <button key={mm.id} onClick={() => navigate(`/properties/${mm.property_id}`)} data-testid={`dash-match-${mm.id}`}
                className="text-left bg-white border border-slate-200 rounded-sm p-4 hover:-translate-y-[2px] transition-transform shadow-[0_8px_30px_rgba(11,37,69,0.04)]">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs uppercase tracking-wide text-slate-400">{t(`matching.party.${mm.party_type}`, mm.party_type)}</span>
                  <span className="text-sm font-semibold text-[#0B2545]">{mm.score}%</span>
                </div>
                <div className="text-sm font-medium text-slate-900 mt-1 truncate">{mm.party_name}</div>
                <div className="text-xs text-slate-500 truncate">{mm.property_title}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {topLocations.length > 0 && (
        <div className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-serif text-xl text-slate-800">{t('places.topLocations', 'Top Locations')}</h3>
            <button onClick={() => navigate('/places')} data-testid="dashboard-all-places" className="text-sm text-[#C5A059] hover:underline">{t('places.title', 'Places')}</button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
            {topLocations.map((loc) => (
              <button key={loc.id} onClick={() => navigate(`/places/${loc.id}`)} data-testid={`top-location-${loc.id}`}
                className="group relative h-40 rounded-sm overflow-hidden border border-slate-200 text-left">
                <img src={placeImage(loc)} alt={loc.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B2545] via-[#0B2545]/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-3 text-white">
                  <div className="font-serif text-sm leading-tight">{loc.name}</div>
                  <div className="text-[11px] text-slate-200">{loc.property_count} · {eurCompact(loc.portfolio_value)}</div>
                  <div className="flex gap-1 mt-1 text-[9px]">
                    <span className="px-1 py-0.5 rounded-sm bg-emerald-500/80">{loc.status_counts?.Available || 0}</span>
                    <span className="px-1 py-0.5 rounded-sm bg-amber-500/80">{loc.status_counts?.Reserved || 0}</span>
                    <span className="px-1 py-0.5 rounded-sm bg-slate-500/80">{loc.status_counts?.Sold || 0}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-sm p-6 shadow-[0_8px_30px_rgba(11,37,69,0.05)]">
          <h3 className="font-serif text-xl text-slate-800 mb-4">{t('monthlyPerformance', 'Monthly Performance')}</h3>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={data.monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" />
              <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} />
              <YAxis stroke="#94a3b8" fontSize={12} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 4, border: t('1pxSolidE2e8f0', '1px solid #e2e8f0') }} />
              <Line type="monotone" dataKey="added" name="Properties added" stroke={NAVY} strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="completed" name="Deals completed" stroke={GOLD} strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-6 shadow-[0_8px_30px_rgba(11,37,69,0.05)]">
          <h3 className="font-serif text-xl text-slate-800 mb-4">{t('byCategory', 'By Category')}</h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={data.by_category} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} innerRadius={50}>
                {data.by_category.map((e, i) => <Cell key={i} fill={PIE[i % PIE.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: 4, border: t('1pxSolidE2e8f0', '1px solid #e2e8f0') }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <div className="bg-white border border-slate-200 rounded-sm p-6 shadow-[0_8px_30px_rgba(11,37,69,0.05)]">
          <h3 className="font-serif text-xl text-slate-800 mb-4">{t('byDistrict', 'By District')}</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.by_district}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" />
              <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} />
              <YAxis stroke="#94a3b8" fontSize={12} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 4, border: t('1pxSolidE2e8f0', '1px solid #e2e8f0') }} />
              <Bar dataKey="value" fill={NAVY} radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {Array.isArray(data.recent_activity) && (
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-sm p-6 shadow-[0_8px_30px_rgba(11,37,69,0.05)]">
            <h3 className="font-serif text-xl text-slate-800 mb-4">{t('recentActivity', 'Recent Activity')}</h3>
            <div className="space-y-3" data-testid="recent-activity">
              {data.recent_activity.length === 0 && <p className="text-slate-400 text-sm">{t('noActivityYet', 'No activity yet.')}</p>}
              {data.recent_activity.map((a, i) => (
                <div key={i} className="flex items-start gap-3 pb-3 border-b border-slate-100 last:border-0">
                  <div className="w-8 h-8 rounded-sm bg-[#0B2545]/10 flex items-center justify-center text-xs font-semibold text-[#0B2545] flex-shrink-0">
                    {(a.user_name || "S").charAt(0)}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm text-slate-700">
                      <span className="font-medium">{a.user_name}</span> {a.action} {a.entity}{" "}
                      {a.detail && <span className="text-slate-500">{t('detail', '— {{detail}}', { detail: a.detail })}</span>}
                    </div>
                    <div className="text-xs text-slate-400">{fdatetime(a.created_at)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
